import { useContext } from 'react'
import { UserContext } from '../../../../../common/context/User'
import {
  BillingGatewayPlatform,
  BillingInterval,
} from '../../../../../common/types'
import type { PricingTier } from '../../../../../common/types'
import {
  getSubscriptionInterval,
  getSubscriptionPlanData,
} from '../../../../../common/utils/billing'
import {
  getUserBillingData,
  isFreeUser,
  isOnActiveTrial,
  isRevenueCatBillingGateway,
} from '../../../../../common/utils/user'
import { usePlanSelectorQuery } from '../../../../PlanSelector/hooks/usePlanSelectorQuery'
import type { BillingType } from '../types'

type UsePlanInformationReturn = {
  planInterval: BillingInterval
  quantity: number
  pricePerQuantity: number
  planId: string
  billingType: BillingType
  isPaidRevenueCat: boolean
  gatewayPlatformDeviceName: string
  gatewayPlatformStoreName: string
  pricingTiers?: Array<PricingTier>
}

const getGatewayPlatformDeviceName = (args: {
  gatewayPlaform?: BillingGatewayPlatform | null
}): string => {
  const { gatewayPlaform = BillingGatewayPlatform.Web } = args

  if (gatewayPlaform === BillingGatewayPlatform.Apple) {
    return 'iOS'
  }

  if (gatewayPlaform === BillingGatewayPlatform.Android) {
    return 'Android'
  }

  return 'Web'
}

const getGatewayPlatformStoreName = (args: {
  gatewayPlaform?: BillingGatewayPlatform | null
}): string => {
  const { gatewayPlaform = BillingGatewayPlatform.Web } = args

  if (gatewayPlaform === BillingGatewayPlatform.Apple) {
    return 'App Store'
  }

  if (gatewayPlaform === BillingGatewayPlatform.Android) {
    return 'Play Store'
  }

  return 'Web'
}

const usePlanInformation = (): UsePlanInformationReturn => {
  const user = useContext(UserContext)
  const { data: planSelectorData } = usePlanSelectorQuery()

  const isFree = isFreeUser(user)
  const isOnTrial = isOnActiveTrial(user)
  const billingData = getUserBillingData(user)
  const currentPlan = getSubscriptionPlanData(billingData)
  const planInterval =
    getSubscriptionInterval(billingData) || BillingInterval.Month
  const isPaidRevenueCat = isRevenueCatBillingGateway(user)
  const gatewayPlaform =
    user.currentOrganization?.billing?.gateway?.gatewayPlatform

  const billingType = isFree ? 'free' : isOnTrial ? 'trial' : 'paid'

  const pricingTiers = planSelectorData?.subscription.plan.pricing
  // unitPrice is returned in cents; the modal displays dollars.
  const pricePerQuantity = (pricingTiers?.[0]?.unitPrice ?? 0) / 100

  return {
    planInterval,
    planId: currentPlan?.id || 'team',
    quantity: planSelectorData?.subscription.quantity ?? 0,
    pricePerQuantity,
    billingType,
    isPaidRevenueCat,
    gatewayPlatformDeviceName: getGatewayPlatformDeviceName({ gatewayPlaform }),
    gatewayPlatformStoreName: getGatewayPlatformStoreName({ gatewayPlaform }),
    pricingTiers,
  }
}

export default usePlanInformation
